import Button from "components/common/buttons/buttons";
import { FixedButtonContainer } from "components/common/buttons/FixedButtons";
import TopHeader from "components/common/header";
import { Forward } from "components/common/Icons";
import CacheModal from "components/templates/common/Portfolio/CacheModal";
import { usePortfolioFlow } from "lib/analytics/react/hooks";
import { TrackedModalProvider } from "lib/analytics/react/modal";
import moment from "moment-timezone";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { TYPO } from "styles/Typo";

function PortfolioLanding({ isMobile }: { isMobile: boolean }) {
  const router = useRouter();
  const { t } = useTranslation("portfolio");
  const portfolioFlow = usePortfolioFlow();
  const contents = [
    {
      title: t("landingQ1Title"),
      description: t("landingQ1Desc"),
      icon: <Icon1 />,
    },
    {
      title: t("landingQ2Title"),
      description: t("landingQ2Desc"),
      icon: <Icon2 />,
    },
    {
      title: t("landingQ3Title"),
      description: t("landingQ3Desc"),
      icon: <Icon3 />,
    },
  ];
  const { data: session, status }: any = useSession();
  const [isExistValidCache, setIsExistValidCache] = useState<false | string>(
    false
  );

  useEffect(() => {
    if (status === "loading") return;
    const isMemember = status === "authenticated";

    // 회원--------------------
    if (isMemember) return;

    // 비회원--------------------
    const hashId = localStorage.getItem("pf-hashId");
    if (!hashId) return;
    const result = localStorage.getItem(`pf-${hashId}`);
    if (result) {
      const parseResult = JSON.parse(result);
      const expireAt = parseResult.expireAt;
      if (
        expireAt >
        moment()
          .tz("Asia/Seoul")
          .subtract(5, "minute")
          .format("YYYY-MM-DD HH:mm:ss")
      ) {
        setTimeout(() => {
          setIsExistValidCache(hashId);
        }, 1000);
      }
    }
  }, [status]);

  // 신규 티저 시작
  const teaserStartHandler = (from: "bottom_cta" | "retry_modal") => {
    portfolioFlow.start();
    router.push("/portfolio/new/add-assets");
  };

  // 계속보기 클릭 시
  const viewHandler = () => {
    if (!isExistValidCache) return;
    router.push(`/portfolio/teaser/${isExistValidCache}?type=score`);
  };

  return (
    <main className="min-w-[360px] max-w-[800px] w-screen bg-white min-h-screen pb-[100px] ">
      <TopHeader
        clickHandler={() => {
          router.push("/?type=explore");
        }}
      />
      <div className="pt-4 px-6 flex flex-col items-center  gap-2">
        <p className={`${TYPO.TYPO5_1_MD} text-blue50`}>
          {t("landingEyebrow")}
        </p>
        <p
          className={`${TYPO.TYPO2_SB} text-gray90 whitespace-pre-line text-center`}
        >
          {t("landingTitle")}
        </p>
      </div>
      <div className="h-6" />
      <div
        className="flex items-center justify-center h-[253px]"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, var(--blue10, #C9E2FF) 51.48%, rgba(232, 243, 255, 0.00) 100%)",
        }}
      >
        <div className="bg-bg rounded-[24px]  border-[2px] border-hairline flex flex-col gap-4 pt-9 justify-between h-[253px]">
          <div className="flex flex-col items-center text-center ">
            <p className={`${TYPO.TYPO4_SB} text-gray90`}>
              {t("landingPassTitle")}
            </p>
            <p className={`${TYPO.TYPO7_1_MD} text-gray60`}>
              {t("landingPassSub")}
            </p>
          </div>
          <Image
            src="/images/portfoliolanding.png"
            alt="portfolio landing"
            height={144}
            width={217}
            unoptimized
          />
        </div>
      </div>
      <div className="h-6" />

      <div className="py-3 flex flex-col">
        {contents.map((item, index) => {
          return (
            <div className="py-2 px-6 flex items-center gap-3" key={index}>
              <div className="w-[30px] h-[30px] bg-gray5 rounded-full">
                {item.icon}
              </div>
              <div className=" grow">
                <p className={`${TYPO.TYPO7_1_MD} text-gray60`}>{item.title}</p>
                <p className={`${TYPO.TYPO5_1_SB} text-gray90`}>
                  {item.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <FixedButtonContainer isMobile={isMobile}>
        <div className="w-full">
          <Button
            type="primary"
            size="xl"
            text={t("landingCta")}
            clickHandler={() => {
              teaserStartHandler("bottom_cta");
            }}
            btnClass="w-full"
            dataTestId="create-portfolio-button"
            dataGtmId="pf-landing-btn"
          />
        </div>
      </FixedButtonContainer>
      <TrackedModalProvider
        modalId="portfolio_modal_existing_result_view"
        isOpen={!!isExistValidCache}
      >
        {isExistValidCache && (
          <CacheModal
            teaserStartHandler={teaserStartHandler}
            setModalOpen={setIsExistValidCache}
            viewHandler={viewHandler}
          />
        )}
      </TrackedModalProvider>
    </main>
  );
}

const Icon1 = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
  >
    <g clipPath="url(#clip0_22529_22498)">
      <path
        d="M8.04975 25.5117C11.2529 25.5117 13.8495 20.8063 13.8495 15.0019C13.8495 9.19756 11.2529 4.49219 8.04975 4.49219C4.84664 4.49219 2.25 9.19756 2.25 15.0019C2.25 20.8063 4.84664 25.5117 8.04975 25.5117Z"
        fill="#E5E8EB"
      />
      <path
        d="M21.9482 25.5117C25.1513 25.5117 27.7479 20.8063 27.7479 15.0019C27.7479 9.19756 25.1513 4.49219 21.9482 4.49219C18.7451 4.49219 16.1484 9.19756 16.1484 15.0019C16.1484 20.8063 18.7451 25.5117 21.9482 25.5117Z"
        fill="#E5E8EB"
      />
      <path
        d="M6.56306 17.9308C8.1814 17.9308 9.49331 16.6189 9.49331 15.0006C9.49331 13.3822 8.1814 12.0703 6.56306 12.0703C4.94473 12.0703 3.63281 13.3822 3.63281 15.0006C3.63281 16.6189 4.94473 17.9308 6.56306 17.9308Z"
        fill="#191F28"
      />
      <path
        d="M20.7818 17.9308C22.4001 17.9308 23.7121 16.6189 23.7121 15.0006C23.7121 13.3822 22.4001 12.0703 20.7818 12.0703C19.1635 12.0703 17.8516 13.3822 17.8516 15.0006C17.8516 16.6189 19.1635 17.9308 20.7818 17.9308Z"
        fill="#191F28"
      />
      <path
        d="M5.36456 14.6764C5.75144 14.6764 6.06506 14.3628 6.06506 13.9759C6.06506 13.589 5.75144 13.2754 5.36456 13.2754C4.97769 13.2754 4.66406 13.589 4.66406 13.9759C4.66406 14.3628 4.97769 14.6764 5.36456 14.6764Z"
        fill="white"
      />
      <path
        d="M19.6536 14.6764C20.0405 14.6764 20.3541 14.3628 20.3541 13.9759C20.3541 13.589 20.0405 13.2754 19.6536 13.2754C19.2667 13.2754 18.9531 13.589 18.9531 13.9759C18.9531 14.3628 19.2667 14.6764 19.6536 14.6764Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_22529_22498">
        <rect width="30" height="30" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const Icon2 = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
  >
    <g clipPath="url(#clip0_22529_22511)">
      <path
        d="M25.8787 12.6391C25.9433 12.1831 25.911 11.7144 25.7962 11.2561L23.3663 12.4051C23.958 13.6539 24.2985 15.0444 24.2985 16.5174C24.2985 21.8356 19.9875 26.1474 14.6693 26.1474C9.351 26.1474 5.04 21.8356 5.04 16.5174C5.04 11.1991 9.351 6.88811 14.6693 6.88811V4.09961C13.8322 4.10186 13.0628 4.38686 12.4493 4.86461C11.9745 5.23436 11.3865 5.39186 10.791 5.30711C10.0155 5.19761 9.1995 5.33486 8.46825 5.75711C7.74 6.17786 7.2135 6.81236 6.92025 7.53536C6.69375 8.09261 6.26325 8.52311 5.70525 8.74811C4.9785 9.04061 4.341 9.56786 3.91875 10.2991C3.498 11.0274 3.36 11.8396 3.46725 12.6129C3.5505 13.2091 3.39225 13.7971 3.02175 14.2704C2.53875 14.8869 2.25 15.6631 2.25 16.5069C2.25 17.3506 2.5365 18.1209 3.01575 18.7366C3.3855 19.2114 3.543 19.7994 3.45825 20.3949C3.34875 21.1704 3.48675 21.9864 3.90825 22.7176C4.329 23.4459 4.9635 23.9724 5.6865 24.2656C6.24375 24.4921 6.67425 24.9226 6.89925 25.4806C7.19175 26.2074 7.71975 26.8449 8.45025 27.2671C9.1785 27.6879 9.9915 27.8259 10.764 27.7186C11.3603 27.6354 11.9475 27.7936 12.4215 28.1641C13.038 28.6471 13.8143 28.9359 14.658 28.9359C15.5018 28.9359 16.272 28.6494 16.8878 28.1701C17.3625 27.8004 17.9505 27.6429 18.546 27.7276C19.3215 27.8371 20.1375 27.6991 20.8688 27.2776C21.597 26.8569 22.1235 26.2224 22.4167 25.4994C22.6432 24.9421 23.0738 24.5116 23.6318 24.2866C24.3585 23.9941 24.996 23.4669 25.4182 22.7356C25.839 22.0074 25.977 21.1944 25.8698 20.4219C25.7865 19.8256 25.9448 19.2376 26.3153 18.7644C26.7983 18.1479 27.087 17.3716 27.087 16.5279C27.087 15.6841 26.8005 14.9139 26.3213 14.2981C25.9515 13.8234 25.794 13.2354 25.8787 12.6399V12.6391Z"
        fill="#965220"
      />
      <path
        d="M14.6687 6.89138C9.22595 6.89138 4.8377 11.4071 5.0462 16.8964C5.23445 21.8531 9.33695 25.9556 14.2937 26.1431C19.7829 26.3516 24.2979 21.9634 24.2979 16.5206C24.2979 15.0476 23.9574 13.6571 23.3657 12.4084L15.4884 16.1329C15.1074 16.3129 14.6679 16.0354 14.6679 15.6131V6.89062L14.6687 6.89138Z"
        fill="#FFB95E"
      />
      <path
        d="M25.3227 9.37341L27.7528 8.22441C27.6725 7.90341 27.5585 7.58616 27.3845 7.28466C26.9637 6.55641 26.3293 6.02991 25.6063 5.73666C25.049 5.51016 24.6185 5.07966 24.3935 4.52166C24.101 3.79491 23.5738 3.15741 22.8425 2.73516C22.1142 2.31441 21.3013 2.17641 20.5288 2.28366C19.9325 2.36691 19.3453 2.20866 18.8713 1.83816C18.2548 1.35516 17.4785 1.06641 16.6348 1.06641C16.6318 1.06641 16.628 1.06716 16.625 1.06716V3.85566C20.4703 3.85566 23.7785 6.11466 25.3227 9.37266V9.37341Z"
        fill="#965220"
      />
      <path
        d="M16.625 3.85547V12.578C16.625 12.9995 17.0637 13.2777 17.4455 13.0977L25.3227 9.37322C23.7792 6.11522 20.4703 3.85547 16.625 3.85547Z"
        fill="#FFB95E"
      />
      <path
        d="M10.1674 15.7743C10.8165 15.7743 11.3427 15.2482 11.3427 14.5991C11.3427 13.95 10.8165 13.4238 10.1674 13.4238C9.51837 13.4238 8.99219 13.95 8.99219 14.5991C8.99219 15.2482 9.51837 15.7743 10.1674 15.7743Z"
        fill="#BA5300"
      />
      <path
        d="M19.8003 10.1005C20.4493 10.1005 20.9755 9.57432 20.9755 8.92525C20.9755 8.27618 20.4493 7.75 19.8003 7.75C19.1512 7.75 18.625 8.27618 18.625 8.92525C18.625 9.57432 19.1512 10.1005 19.8003 10.1005Z"
        fill="#BA5300"
      />
      <path
        d="M19.3784 20.5204C20.0274 20.5204 20.5536 19.9942 20.5536 19.3452C20.5536 18.6961 20.0274 18.1699 19.3784 18.1699C18.7293 18.1699 18.2031 18.6961 18.2031 19.3452C18.2031 19.9942 18.7293 20.5204 19.3784 20.5204Z"
        fill="#BA5300"
      />
      <path
        d="M12.5112 22.5927C13.1603 22.5927 13.6864 22.0665 13.6864 21.4174C13.6864 20.7684 13.1603 20.2422 12.5112 20.2422C11.8621 20.2422 11.3359 20.7684 11.3359 21.4174C11.3359 22.0665 11.8621 22.5927 12.5112 22.5927Z"
        fill="#BA5300"
      />
    </g>
    <defs>
      <clipPath id="clip0_22529_22511">
        <rect width="30" height="30" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const Icon3 = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
  >
    <g clip-path="url(#clip0_22529_22526)">
      <path
        d="M12.5469 4.97609L2.1106 23.0526C1.01935 24.9426 2.3836 27.3051 4.5661 27.3051H25.4394C27.6219 27.3051 28.9854 24.9426 27.8949 23.0526L17.4579 4.97609C16.3666 3.08609 13.6381 3.08609 12.5469 4.97609Z"
        fill="#FFCD00"
      />
      <path
        d="M15.0034 20.6621C14.2204 20.6621 13.5859 21.2966 13.5859 22.0796C13.5859 22.8626 14.2204 23.4971 15.0034 23.4971C15.7864 23.4971 16.4209 22.8626 16.4209 22.0796C16.4209 21.2966 15.7864 20.6621 15.0034 20.6621Z"
        fill="#303D4C"
      />
      <path
        d="M15.0047 18.8102C14.3417 18.8102 13.8047 18.2732 13.8047 17.6102V11.7039C13.8047 11.0409 14.3417 10.5039 15.0047 10.5039C15.6677 10.5039 16.2047 11.0409 16.2047 11.7039V17.6102C16.2047 18.2732 15.6677 18.8102 15.0047 18.8102Z"
        fill="#303D4C"
      />
    </g>
    <defs>
      <clipPath id="clip0_22529_22526">
        <rect width="30" height="30" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
export default PortfolioLanding;
